import React, { useRef } from 'react';
import { useDgptAlertsFlag } from '../../utils/featureFlags';
import { useAorText } from 'utils/aorTerminology';
import { useCreateAorsFromGeoJSON } from 'hooks/useCreateAorsFromGeoJSON';
import { useIsDev } from 'hooks/useIsDev';
import { toast } from 'react-toastify';
import { parseError } from 'utils/parseTypedErrorMessage';
import { noAutoClose } from 'assets/data/config';
import { AorManager } from './aorEditor/AorManager';

/**
 * CgGeojsonUploadButton — invisible utility button for manual endpoint testing.
 *
 * Usage (browser console):
 * document.getElementById('cg-geojson-upload').click();
 */
const CgGeojsonUploadButton: React.FC = () => {
  const aorText = useAorText();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { mutate: createAorsFromGeoJSON } = useCreateAorsFromGeoJSON();

  const isDev = useIsDev();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (!files.length) return;

    // Reset so the same file(s) can be re-selected if needed
    e.target.value = '';

    createAorsFromGeoJSON(files, {
      onSuccess: () => {
        toast.success('Debug upload succeeded');
      },
      onError: (error) => {
        const message = parseError(error) ?? 'Debug upload failed.';
        toast.error(message, noAutoClose);
      },
    });
  };

  return (
    <div
      style={
        isDev
          ? {}
          : { position: 'absolute', width: 0, height: 0, overflow: 'hidden' }
      }
      aria-hidden={isDev ? 'false' : 'true'}
    >
      <h5>{aorText('AOR upload')}</h5>
      <input
        ref={fileInputRef}
        id="cg-geojson-upload"
        type="file"
        multiple
        onChange={handleFileChange}
        tabIndex={-1}
      />
    </div>
  );
};

const ManageLocation: React.FC = () => {
  const dgptAlertsEnabled = useDgptAlertsFlag();
  return (
    <div className="dark tw:rounded-lg tw:border tw:border-border tw:bg-background tw:p-4">
      <AorManager showAlertsWarning={dgptAlertsEnabled} />
      <CgGeojsonUploadButton />
    </div>
  );
};

export default ManageLocation;
